<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import type { GenderType, GenderPayloadType } from "@/common/types/gender";

import { baseInput, baseSelect, baseTextEditor } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "salve:data"]);

const props = defineProps<{
  data?: GenderType | null;
  isPatch?: boolean;
}>();

const DEFAULT_DATA = {
  name: "",
  status: "ACTIVE",
  description: "",
};

const payload = ref<GenderPayloadType>({ ...DEFAULT_DATA });

const optionStatus = computed(() => [
  { value: "ACTIVE", option: t("common.labels.active") },
  { value: "INACTIVE", option: t("common.labels.inactive") },
]);

function update(key: keyof typeof payload.value) {
  if (props.isPatch) {
    emit("update:data", { [key]: payload.value[key] });
    return;
  }

  emit("update:data", payload.value);
}

function resumeData() {
  if (!props.data) return;

  const { name, status, description } = props.data;

  payload.value = {
    ...payload.value,
    name,
    status,
    description,
  };
}

function maskSlug(data: string) {
  return data?.replaceAll(" ", "-")?.toLowerCase();
}

const debouceUpdate = debounce(update, 300);

watchEffect(() => {
  resumeData();
});
</script>

<template>
  <section class="form-gender">
    <base-input
      v-model="payload.name"
      :value="payload.name"
      :placeholder="t('gender.placeholders.name')"
      required
      @update:modelValue="debouceUpdate('name')"
    >
      <template #label> {{ t("gender.labels.name") }} * </template>
    </base-input>

    <base-select
      v-model="payload.status"
      :value="payload.status"
      :options="optionStatus"
      :is-empty="false"
      @change="debouceUpdate('status')"
    >
      <template #label> {{ t("common.labels.status") }} *</template>
    </base-select>

    <base-text-editor
      v-model="payload.description"
      @update:modelValue="debouceUpdate('description')"
    >
      <template #label> {{ t("gender.labels.description") }}</template>
    </base-text-editor>
  </section>
</template>

<style lang="scss" scoped>
.form-gender {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}
</style>
